import { SyntheticEvent, useCallback } from 'react';
import { UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Input, Select, RadioList } from '@treezor/design-system';

import { AdditonalDataLayout } from '@/features/user/components/CreateUser/layout/AddtionalData';
import { AddressSummary } from '@/features/user/components/CreateUser/AddressSummary';
import { Section, TwoColumns, CustomColumns } from '@/features/user/components/CreateUser/layout/ContentLayout';
import { CreateUserEmptyComponent } from '@/features/user/components/CreateUser/layout/CreateUserEmptyComponent';
import { useFieldOptions } from '@/features/user/hooks/createUser/useFieldOptions';
import { Contact } from '@/features/user/components/CreateUser/Contact';
import { useCreateBusinessUser } from '@/features/user/hooks/createUser/useCreateBusinessUser';
import { ICreateUserInfo, IPostCreateUser } from '@/features/user/contexts/CreateUserContext';

interface IUserInformationProps {
  memberIndex: number;
  memberWatch: ICreateUserInfo;
  handleChange: (memberIndex: number, key: keyof ICreateUserInfo, value: string) => void;
  hasValidField: (inputFields: string[]) => boolean;
  register: UseFormRegister<IPostCreateUser>;
}
export function CreateUserInformation({
  memberIndex,
  memberWatch,
  handleChange: handleChangeMember,
  hasValidField,
  register,
}: IUserInformationProps) {
  const { t } = useTranslation();

  const { countryCodeOptions, persoAndBusinessAddressOptions, titleOptions } = useFieldOptions();

  const memberKey = `members.${memberIndex}` as const;

  const { user, inputMaxLength } = useCreateBusinessUser();

  const hasMiddleNames = hasValidField([`${memberKey}.middleName1`, `${memberKey}.middleName2`]);
  const hasSecondaryAddress = hasValidField([
    `${memberKey}.secondaryAddress1`,
    `${memberKey}.secondaryAddress2`,
    `${memberKey}.secondaryAddress3`,
    `${memberKey}.secondaryPostcode`,
    `${memberKey}.secondaryCity`,
    `${memberKey}.secondaryCountry`,
  ]);

  const handleChange = useCallback(
    (field: keyof ICreateUserInfo, value: string | SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const _value = typeof value === 'string' ? value : value?.currentTarget?.value;
      handleChangeMember(memberIndex, field, _value);
    },
    [handleChangeMember, memberIndex],
  );

  const onChangeIsSameAsPrincipalAddress = useCallback(
    (value: 'yes' | 'no') => {
      handleChange('isSameAsPrincipalAddress', value);
    },
    [handleChange],
  );

  return (
    <div className="flex flex-col gap-5 pt-7">
      <Section title={t('pages.dashboard.createUser.form.blocks.mainInformation')} key={`main-info-${memberIndex}`}>
        <CustomColumns columns={[1, 2, 2]}>
          <Select
            label={t('pages.dashboard.createUser.form.title')}
            options={titleOptions}
            defaultValue={user?.members?.[memberIndex]?.title}
            onChange={(value: string) => handleChange('title', value)}
          />
          <Input
            type="text"
            label={t('pages.dashboard.createUser.form.lastname')}
            required
            value={memberWatch.lastname}
            disabled
          />
          <Input
            type="text"
            label={t('pages.dashboard.createUser.form.firstname')}
            required
            value={memberWatch.firstname}
            disabled
          />
        </CustomColumns>
        <AdditonalDataLayout
          shouldShow={!!hasMiddleNames}
          label={t('pages.dashboard.createUser.form.actions.addMiddleNames')}
        >
          <TwoColumns>
            <Input
              type="text"
              label={t('pages.dashboard.createUser.form.middleName')}
              {...register(`${memberKey}.middleName1`)}
              onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                handleChange('middleName1', event)
              }
            />
            <Input
              type="text"
              label={t('pages.dashboard.createUser.form.middleName')}
              {...register(`${memberKey}.middleName2`)}
              onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                handleChange('middleName2', event)
              }
            />
          </TwoColumns>
        </AdditonalDataLayout>
      </Section>
      <Section title={t('pages.dashboard.createUser.form.mainAddress')} key={`main-address-${memberIndex}`}>
        <RadioList
          name="isSameAsPrincipalAddress"
          options={persoAndBusinessAddressOptions}
          onChange={(value: 'yes' | 'no') => {
            onChangeIsSameAsPrincipalAddress(value);
          }}
          value={memberWatch.isSameAsPrincipalAddress}
          className="pt-2 gap-3"
          direction="row"
        />
        {memberWatch.isSameAsPrincipalAddress === 'yes' && (
          <AddressSummary
            title={t('pages.dashboard.createUser.form.blocks.address')}
            address1={user?.address1}
            address2={user?.address2}
            address3={user?.address3}
            city={user?.city}
            country={user?.country}
            postcode={user?.postcode}
          />
        )}

        {memberWatch.isSameAsPrincipalAddress === 'no' && (
          <>
            <Select
              label={t('pages.currentProfiles.profile.contact.country')}
              options={countryCodeOptions}
              withSearch
              defaultValue={user?.members?.[memberIndex]?.country}
              onChange={(value: string) => {
                handleChange('country', value);
              }}
              noOptionsComponent={<CreateUserEmptyComponent />}
            />
            <TwoColumns>
              <Input
                value={memberWatch.address1}
                type="text"
                label={t('pages.dashboard.createUser.form.address1')}
                {...register(`${memberKey}.address1`)}
                onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleChange('address1', event)
                }
                {...inputMaxLength(memberWatch.address1, 35)}
              />
              <Input
                value={memberWatch.address2}
                type="text"
                label={t('pages.dashboard.createUser.form.address2')}
                {...register(`${memberKey}.address2`)}
                onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleChange('address2', event)
                }
                {...inputMaxLength(memberWatch.address2, 35)}
              />
            </TwoColumns>
            <Input
              value={memberWatch.address3}
              type="text"
              label={t('pages.dashboard.createUser.form.address3')}
              {...register(`${memberKey}.address3`)}
              onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                handleChange('address3', event)
              }
              {...inputMaxLength(memberWatch.address3, 35)}
            />
            <TwoColumns>
              <Input
                type="text"
                label={t('pages.currentProfiles.profile.contact.postcode')}
                {...register(`${memberKey}.postcode`)}
                onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleChange('postcode', event)
                }
                value={memberWatch?.postcode}
              />
              <Input
                type="text"
                label={t('pages.currentProfiles.profile.contact.city')}
                {...register(`${memberKey}.city`)}
                onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleChange('city', event)
                }
                value={memberWatch?.city}
              />
            </TwoColumns>
          </>
        )}

        <AdditonalDataLayout
          shouldShow={!!hasSecondaryAddress}
          label={t('pages.dashboard.createUser.form.actions.addSecondaryAddress')}
        >
          <div>
            <span className="typo-h6-bold">{t('pages.dashboard.createUser.form.secondaryAddress')}</span>
          </div>
          <Select
            label={t('pages.currentProfiles.profile.contact.country')}
            options={countryCodeOptions}
            withSearch
            defaultValue={user?.members?.[memberIndex]?.secondaryCountry}
            onChange={(value: string) => handleChange('secondaryCountry', value)}
            noOptionsComponent={<CreateUserEmptyComponent />}
          />
          <TwoColumns>
            <Input
              value={memberWatch.secondaryAddress1}
              type="text"
              label={t('pages.dashboard.createUser.form.address1')}
              {...register(`${memberKey}.secondaryAddress1`)}
              onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                handleChange('secondaryAddress1', event)
              }
              {...inputMaxLength(memberWatch.secondaryAddress1, 35)}
            />
            <Input
              type="text"
              value={memberWatch.secondaryAddress2}
              label={t('pages.dashboard.createUser.form.address2')}
              {...register(`${memberKey}.secondaryAddress2`)}
              onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                handleChange('secondaryAddress2', event)
              }
              {...inputMaxLength(memberWatch.secondaryAddress2, 35)}
            />
          </TwoColumns>
          <Input
            type="text"
            value={memberWatch.secondaryAddress3}
            label={t('pages.dashboard.createUser.form.address3')}
            {...register(`${memberKey}.secondaryAddress3`)}
            onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleChange('secondaryAddress3', event)
            }
            {...inputMaxLength(memberWatch.secondaryAddress3, 35)}
          />
          <TwoColumns>
            <Input
              type="text"
              label={t('pages.currentProfiles.profile.contact.postcode')}
              {...register(`${memberKey}.secondaryPostcode`)}
              onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                handleChange('secondaryPostcode', event)
              }
              defaultValue={user?.secondaryPostcode}
            />
            <Input
              type="text"
              label={t('pages.currentProfiles.profile.contact.city')}
              {...register(`${memberKey}.secondaryCity`)}
              onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                handleChange('secondaryCity', event)
              }
            />
          </TwoColumns>
        </AdditonalDataLayout>
      </Section>
      <Section title={t('pages.dashboard.createUser.form.blocks.contact')} key={`contact-${memberIndex}`}>
        <TwoColumns>
          <Contact
            label={t('pages.dashboard.createUser.form.phone')}
            value={memberWatch.phone}
            initialValue={user?.members?.[memberIndex]?.phone}
            handleValueChange={(value: string, _) => handleChange('phone', value)}
            {...register(`${memberKey}.phone`)}
          />
          <Contact
            label={t('pages.dashboard.createUser.form.mobile')}
            value={memberWatch.mobile}
            initialValue={user?.members?.[memberIndex]?.mobile}
            handleValueChange={(value: string, _) => handleChange('mobile', value)}
            {...register(`${memberKey}.mobile`)}
          />
        </TwoColumns>
      </Section>
    </div>
  );
}
